<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  identifiers: {
    type: Array,
    default: () => [],
  },
  phoneNumber: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const USER_BSUID_PATTERN = /^[A-Z]{2}\.(?!ENT\.)[A-Za-z0-9]{1,128}$/;

// whatsapp-bsuid-coexistence.UI.2 and whatsapp-bsuid-coexistence.UI.5:
// phone-shaped and parent-scoped source IDs never reach the row.
const bsuidIdentifiers = computed(() =>
  props.identifiers.filter(identifier =>
    USER_BSUID_PATTERN.test(identifier?.source_id || '')
  )
);

// whatsapp-bsuid-coexistence.UI.1, whatsapp-bsuid-coexistence.UI.3, and
// whatsapp-bsuid-coexistence.UI.4: the rollout flag, a user BSUID alias, and a
// phone-less contact are all required before anything renders.
const shouldRender = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_WHATSAPP_BSUID_INBOUND_ALIASES') &&
    !props.phoneNumber &&
    bsuidIdentifiers.value.length > 0
);

const truncateIdentifier = sourceId =>
  sourceId.length > 12 ? `${sourceId.slice(0, 12)}…` : sourceId;

const inboxTooltip = inboxId =>
  t('CONTACT_PANEL.LINKED_IDENTIFIER_INBOX', { inboxId });

const copyIdentifier = async sourceId => {
  await copyTextToClipboard(sourceId);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div class="contents">
    <div
      v-if="shouldRender"
      data-testid="linked-identifiers-row"
      class="contents"
    >
      <div
        v-for="identifier in bsuidIdentifiers"
        :key="`${identifier.inbox_id}:${identifier.source_id}`"
        data-testid="linked-identifier"
        class="flex items-center w-full min-w-0 h-5 gap-2 text-n-slate-11 ltr:-ml-1 rtl:-mr-1"
      >
        <svg
          aria-hidden="true"
          class="flex-shrink-0 size-3.5 ltr:ml-1 rtl:mr-1"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
          />
          <path
            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
          />
        </svg>
        <span
          v-tooltip.top="inboxTooltip(identifier.inbox_id)"
          class="min-w-0 overflow-hidden font-mono text-sm whitespace-nowrap text-ellipsis"
          :title="identifier.source_id"
        >
          {{ truncateIdentifier(identifier.source_id) }}
        </span>
        <NextButton
          v-tooltip.top="t('CONTACT_PANEL.COPY_LINKED_IDENTIFIER')"
          ghost
          xs
          slate
          class="flex-shrink-0 ltr:-ml-1 rtl:-mr-1"
          icon="i-lucide-clipboard"
          :aria-label="t('CONTACT_PANEL.COPY_LINKED_IDENTIFIER')"
          @click="copyIdentifier(identifier.source_id)"
        />
      </div>
    </div>
  </div>
</template>
